import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  IconButton,
  Alert,
  Skeleton,
  Divider,
  Avatar,
  Grid,
} from '@mui/material';
import {
  Agriculture,
  Search,
  FilterList,
  LocationOn,
  Business,
  Phone,
  Email,
  Verified,
  Clear,
} from '@mui/icons-material';
import { farmService, Farm } from '../../../services/farm/farmService';
import useAuth from '../../../hooks/useAuth';
import { DESIGN_TOKENS } from '../../../design-system/tokens/colors';

// Farm search filters interface
export interface FarmSearchFilters {
  name?: string;
  farm_type?: 'dairy' | 'beef' | 'mixed' | 'other';
  owner_id?: string;
  registration_number?: string;
  location?: {
    region?: string;
    country_code?: string;
  };
  area_hectares?: {
    min?: number;
    max?: number;
  };
  capacity_head?: {
    min?: number;
    max?: number;
  };
  certifications?: string[];
  is_active?: boolean;
}

interface FarmSearchViewProps {
  initialFilters?: FarmSearchFilters;
  onFarmSelect?: (farm: Farm) => void;
}

const FarmSearchView: React.FC<FarmSearchViewProps> = ({ 
  initialFilters = {}, 
  onFarmSelect 
}) => {
  const { authState } = useAuth();
  const [farms, setFarms] = useState<Farm[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<FarmSearchFilters>(initialFilters);
  const [showFilters, setShowFilters] = useState(false);

  // Search farms when filters change
  useEffect(() => {
    if (authState.user) {
      searchFarms();
    }
  }, [authState.user]);

  const buildSearchQuery = (searchFilters: FarmSearchFilters) => {
    const conditions: string[] = [];
    const bindVars: any = {
      '@collection': 'Farms'
    };

    // Always filter by current user - only use owner_id
    if (authState.user?.id) {
      // Filter by owner_id only
      conditions.push('doc.owner_id == @user_id');
      bindVars.user_id = authState.user.id;
    } else if (authState.user?.username) {
      // Fallback to username if no id
      conditions.push('doc.owner_id == @username');
      bindVars.username = authState.user.username;
    }

    // Name filter (partial match)
    if (searchFilters.name) {
      conditions.push('LIKE(doc.name, @name, true)');
      bindVars.name = `%${searchFilters.name}%`;
    }

    // Farm type filter
    if (searchFilters.farm_type) {
      conditions.push('doc.farm_type == @farm_type');
      bindVars.farm_type = searchFilters.farm_type;
    }

    // Registration number filter
    if (searchFilters.registration_number) {
      conditions.push('doc.registration_number == @registration_number');
      bindVars.registration_number = searchFilters.registration_number;
    }

    // Location filters
    if (searchFilters.location?.region) {
      conditions.push('doc.location.region == @region');
      bindVars.region = searchFilters.location.region;
    }

    // Area range filter
    if (searchFilters.area_hectares?.min !== undefined) {
      conditions.push('doc.area_hectares >= @min_area');
      bindVars.min_area = searchFilters.area_hectares.min;
    }

    if (searchFilters.area_hectares?.max !== undefined) {
      conditions.push('doc.area_hectares <= @max_area');
      bindVars.max_area = searchFilters.area_hectares.max;
    }

    // Capacity range filter
    if (searchFilters.capacity_head?.min !== undefined) {
      conditions.push('doc.capacity_head >= @min_capacity');
      bindVars.min_capacity = searchFilters.capacity_head.min;
    }

    if (searchFilters.capacity_head?.max !== undefined) {
      conditions.push('doc.capacity_head <= @max_capacity');
      bindVars.max_capacity = searchFilters.capacity_head.max;
    }

    // Certifications filter
    if (searchFilters.certifications && searchFilters.certifications.length > 0) {
      conditions.push('LENGTH(INTERSECTION(doc.certifications, @certifications)) > 0');
      bindVars.certifications = searchFilters.certifications;
    }

    // Active status filter
    if (searchFilters.is_active !== undefined) {
      conditions.push('doc.is_active == @is_active');
      bindVars.is_active = searchFilters.is_active;
    }

    // Build final query
    let query = 'FOR doc IN @@collection';
    
    if (conditions.length > 0) {
      query += ' FILTER ' + conditions.join(' AND ');
    }
    
    query += ' RETURN doc';

    return { query, bind_vars: bindVars };
  };

  const searchFarms = async () => {
    if (!authState.user?.id) {
      setError('Usuário não autenticado');
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      const searchRequest = buildSearchQuery(filters);
      
      const result = await farmService.search(searchRequest);
      
      if (result.success && result.data) {
        const farmsArray = Array.isArray(result.data) ? result.data : [result.data];
        setFarms(farmsArray);
      } else {
        setError(result.error || 'Erro ao buscar fazendas');
        setFarms([]);
      }
    } catch (err: any) {
      console.error('Farm search error:', err);
      setError(err.message || 'Erro inesperado ao buscar fazendas');
      setFarms([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field: keyof FarmSearchFilters, value: any) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
    // Trigger search when filters change
    setTimeout(() => searchFarms(), 100);
  };

  const handleAreaRangeChange = (type: 'min' | 'max', value: string) => {
    const numValue = value ? parseFloat(value) : undefined;
    setFilters(prev => ({
      ...prev,
      area_hectares: {
        ...prev.area_hectares,
        [type]: numValue
      }
    }));
    // Trigger search when filters change
    setTimeout(() => searchFarms(), 100);
  };

  const handleCapacityRangeChange = (type: 'min' | 'max', value: string) => {
    const numValue = value ? parseFloat(value) : undefined;
    setFilters(prev => ({
      ...prev,
      capacity_head: {
        ...prev.capacity_head,
        [type]: numValue
      }
    }));
    // Trigger search when filters change
    setTimeout(() => searchFarms(), 100);
  };

  const clearFilters = () => {
    setFilters({});
    // Trigger search when filters are cleared
    setTimeout(() => searchFarms(), 100);
  };



  // Farm type options
  const getFarmTypeOptions = () => [
    { value: 'dairy', label: 'Gado Leiteiro' },
    { value: 'beef', label: 'Gado de Corte' },
    { value: 'mixed', label: 'Misto (Corte + Leite)' },
    { value: 'other', label: 'Outros Animais' }
  ];

  // Certification options
  const getCertificationOptions = () => [
    'Rastreabilidade SISBOV',
    'BPF (Boas Práticas de Fabricação)',
    'Orgânico',
    'ISO 9001',
    'HACCP',
    'Bem-estar Animal',
    'Sustentabilidade'
  ];

  const getFarmTypeLabel = (type: string | string[]) => {
    const typeMap: { [key: string]: string } = {
      'dairy': 'Gado Leiteiro',
      'beef': 'Gado de Corte',
      'mixed': 'Misto',
      'other': 'Outros'
    };
    
    if (Array.isArray(type)) {
      return type.map(t => typeMap[t] || t).join(', ');
    }
    
    return typeMap[type] || type;
  };

  const renderFarmCard = (farm: Farm) => (
    <Card 
      key={farm.id || farm.owner_id} 
      sx={{ 
        mb: 2, 
        '&:hover': { 
          boxShadow: 3,
          transform: 'translateY(-2px)',
          transition: 'all 0.2s ease-in-out'
        }
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
          <Avatar sx={{ bgcolor: DESIGN_TOKENS.colors.organization.farm, mr: 2 }}>
            <Agriculture />
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: DESIGN_TOKENS.colors.organization.farm }}>
              {farm.name}
            </Typography>
            {farm.registration_number && (
              <Typography variant="body2" color="text.secondary">
                Registro: {farm.registration_number}
              </Typography>
            )}
          </Box>
          <Chip 
            label={getFarmTypeLabel(farm.farm_type)}
            size="small"
            sx={{ 
              backgroundColor: 'rgba(107, 196, 60, 0.12)',
              color: DESIGN_TOKENS.colors.organization.farm
            }}
          />
        </Box>

        <Grid spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <LocationOn sx={{ fontSize: 16, color: '#666', mr: 1 }} />
              <Typography variant="body2">
                {farm.location?.address || 'Endereço não informado'}
              </Typography>
            </Box>
            {farm.location?.region && (
              <Typography variant="body2" color="text.secondary" sx={{ ml: 3 }}>
                {farm.location.region}
              </Typography>
            )}
          </Grid>
          
          <Grid size={{ xs: 12, sm: 6 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <img 
                src="/vaca.svg" 
                alt="Farm" 
                style={{ 
                  width: 16, 
                  height: 16, 
                  marginRight: 8,
                  filter: 'brightness(0) saturate(100%) invert(40%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(102%) contrast(102%)'
                }} 
              />
              <Typography variant="body2">
                {farm.area_hectares ? `${farm.area_hectares} hectares` : 'Área não informada'}
              </Typography>
            </Box>
            {farm.capacity_head && (
              <Typography variant="body2" color="text.secondary" sx={{ ml: 3 }}>
                Capacidade: {farm.capacity_head} cabeças
              </Typography>
            )}
          </Grid>
        </Grid>

        {farm.contact_info && (
          <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #eee' }}>
            <Typography variant="subtitle2" sx={{ mb: 1, color: DESIGN_TOKENS.colors.organization.farm }}>
              Contato
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              {farm.contact_info.phone && (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Phone sx={{ fontSize: 14, color: '#666', mr: 0.5 }} />
                  <Typography variant="body2">{farm.contact_info.phone}</Typography>
                </Box>
              )}
              {farm.contact_info.email && (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Email sx={{ fontSize: 14, color: '#666', mr: 0.5 }} />
                  <Typography variant="body2">{farm.contact_info.email}</Typography>
                </Box>
              )}
            </Box>
          </Box>
        )}

        {farm.certifications && farm.certifications.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, color: DESIGN_TOKENS.colors.organization.farm }}>
              Certificações
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {farm.certifications.map((cert, index) => (
                <Chip
                  key={index}
                  label={cert}
                  size="small"
                  icon={<Verified />}
                  sx={{ 
                    backgroundColor: DESIGN_TOKENS.colors.primary[50],
                    color: DESIGN_TOKENS.colors.primary[700],
                    fontSize: '0.75rem'
                  }}
                />
              ))}
            </Box>
          </Box>
        )}
      </CardContent>
      
      {onFarmSelect && (
        <CardActions>
          <Button 
            size="small" 
            onClick={() => onFarmSelect(farm)}
            sx={{ color: DESIGN_TOKENS.colors.organization.farm }}
          >
            Selecionar
          </Button>
        </CardActions>
      )}
    </Card>
  );

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Agriculture sx={{ color: DESIGN_TOKENS.colors.organization.farm, mr: 2, fontSize: 32 }} />
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600, color: DESIGN_TOKENS.colors.organization.farm }}>
              Buscar Fazendas
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Encontre fazendas disponíveis usando os filtros abaixo
            </Typography>
          </Box>
        </Box>
        <IconButton 
          onClick={() => setShowFilters(!showFilters)}
          sx={{ color: DESIGN_TOKENS.colors.organization.farm }}
        >
          <FilterList />
        </IconButton>
      </Box>

      {/* Filters */}
      {showFilters && (
        <Paper sx={{ p: 3, mb: 3, backgroundColor: DESIGN_TOKENS.colors.background.elevated }}>
          <Typography variant="h6" sx={{ mb: 2, color: DESIGN_TOKENS.colors.organization.farm }}>
            Filtros de Busca
          </Typography>
          
          <Grid spacing={2}>
            {/* Name Search */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Nome da Fazenda"
                placeholder="Digite o nome..."
                value={filters.name || ''}
                onChange={(e) => handleFilterChange('name', e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&.Mui-focused fieldset': { borderColor: DESIGN_TOKENS.colors.organization.farm }
                  }
                }}
              />
            </Grid>

            {/* Farm Type */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Tipo de Fazenda</InputLabel>
                <Select
                  value={filters.farm_type || ''}
                  onChange={(e) => handleFilterChange('farm_type', e.target.value)}
                  label="Tipo de Fazenda"
                >
                  <MenuItem value="">Todos os tipos</MenuItem>
                  {getFarmTypeOptions().map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Area Range */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <TextField
                  label="Área Mín (ha)"
                  type="number"
                  value={filters.area_hectares?.min || ''}
                  onChange={(e) => handleAreaRangeChange('min', e.target.value)}
                  sx={{ flex: 1 }}
                />
                <Typography>-</Typography>
                <TextField
                  label="Área Máx (ha)"
                  type="number"
                  value={filters.area_hectares?.max || ''}
                  onChange={(e) => handleAreaRangeChange('max', e.target.value)}
                  sx={{ flex: 1 }}
                />
              </Box>
            </Grid>

            {/* Capacity Range */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <TextField
                  label="Capacidade Mín"
                  type="number"
                  value={filters.capacity_head?.min || ''}
                  onChange={(e) => handleCapacityRangeChange('min', e.target.value)}
                  sx={{ flex: 1 }}
                />
                <Typography>-</Typography>
                <TextField
                  label="Capacidade Máx"
                  type="number"
                  value={filters.capacity_head?.max || ''}
                  onChange={(e) => handleCapacityRangeChange('max', e.target.value)}
                  sx={{ flex: 1 }}
                />
              </Box>
            </Grid>

            {/* Region */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Região"
                placeholder="Digite a região..."
                value={filters.location?.region || ''}
                onChange={(e) => handleFilterChange('location', { 
                  ...filters.location, 
                  region: e.target.value 
                })}
              />
            </Grid>

            {/* Active Status */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.is_active === undefined ? '' : filters.is_active.toString()}
                  onChange={(e) => {
                    const value = e.target.value;
                    handleFilterChange('is_active', value === '' ? undefined : value === 'true');
                  }}
                  label="Status"
                >
                  <MenuItem value="">Todos</MenuItem>
                  <MenuItem value="true">Ativas</MenuItem>
                  <MenuItem value="false">Inativas</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
            <Button 
              variant="contained" 
              onClick={searchFarms}
              startIcon={<Search />}
              sx={{ 
                backgroundColor: DESIGN_TOKENS.colors.organization.farm,
                '&:hover': { backgroundColor: DESIGN_TOKENS.colors.primary[600] }
              }}
            >
              Buscar
            </Button>
            <Button 
              variant="outlined" 
              onClick={clearFilters}
              startIcon={<Clear />}
              sx={{ 
                borderColor: DESIGN_TOKENS.colors.organization.farm,
                color: DESIGN_TOKENS.colors.organization.farm,
                '&:hover': { borderColor: DESIGN_TOKENS.colors.primary[600], backgroundColor: DESIGN_TOKENS.colors.organization.farm + '0A' }
              }}
            >
              Limpar Filtros
            </Button>
          </Box>
        </Paper>
      )}

      {/* Results */}
      <Box>
        {loading && (
          <Box>
            {[1, 2, 3].map(i => (
              <Skeleton key={i} variant="rectangular" height={200} sx={{ mb: 2, borderRadius: 1 }} />
            ))}
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!loading && !error && (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6" sx={{ color: DESIGN_TOKENS.colors.organization.farm }}>
                Resultados ({farms.length} fazenda{farms.length !== 1 ? 's' : ''})
              </Typography>
            </Box>

            {farms.length === 0 ? (
              <Paper sx={{ p: 4, textAlign: 'center' }}>
                <Agriculture sx={{ fontSize: 48, color: '#ccc', mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                  Nenhuma fazenda encontrada
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Tente ajustar os filtros de busca
                </Typography>
              </Paper>
            ) : (
              <Box>
                {farms.map(renderFarmCard)}
              </Box>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default FarmSearchView;